// Copyright (c) Docugami, Inc. All rights reserved.

import { getDirectoryPath, isAncestor, isChild } from "components/file_management/file_helpers";
import { Logger, LoggerCategory } from "utils/Logger";
import { IObservableCollection, ObservableArray } from "utils/ObservableArray";
import { DgDirectory, FileManager, DgFile, FileProperties, DirectoryStatus, DirectoryProperties } from "./FileManager";

/**
 * File view of a given directory.
 */
export class DirectoryView {
    private readonly _directory: DgDirectory;
    private readonly _items: ObservableArray<DgFile | DgDirectory>;
    private readonly _fileManager: FileManager;
    private readonly _unregister: () => void;
    private readonly _showHidden: boolean;

    get directory(): DgDirectory { return this._directory; }
    get observableItems(): IObservableCollection<DgFile | DgDirectory> { return this._items; }
    get items(): Array<DgFile | DgDirectory> { return [...this._items.storage]; }
    get files(): Array<DgFile> { return this._items.storage.filter(item => item instanceof DgFile) as Array<DgFile> }
    get directories(): Array<DgDirectory> { return this._items.storage.filter(item => item instanceof DgDirectory) as Array<DgDirectory> }

    constructor(fileManager: FileManager, path: string, showHidden: boolean = false) {
        const dirPath = getDirectoryPath(path);
        this._directory = fileManager.getOrCreateDirectory(dirPath);
        this._fileManager = fileManager;
        this._showHidden = showHidden;

        const initialItems = fileManager.items.filter(item => this.isViewItem(dirPath, item.properties));
        this._items = new ObservableArray(initialItems, (item1: DgFile | DgDirectory, item2: DgFile | DgDirectory) => item1.properties.path === item2.properties.path);

        const unregisterAdded = fileManager.onItemAdded.addListener(this.onItemAdded.bind(this));
        const unregisterRemoved = fileManager.onItemRemoved.addListener(this.onItemRemoved.bind(this));
        const unregisterUpdated = fileManager.onFileUpdated.addListener(this.onFileUpdated.bind(this));
        const unregisterStatusChange = this._directory.onStatusChanged.addListener(this.onStatusChanged.bind(this));

        this._unregister = () => {
            unregisterAdded();
            unregisterRemoved();
            unregisterUpdated();
            unregisterStatusChange();
        }
    }

    destructor() {
        this._unregister();
    }

    async initialize(): Promise<Array<DgFile | DgDirectory> | null> {
        return this._fileManager.fetchFiles([this.directory.properties.path]);
    }

    private onItemAdded(item: DgFile | DgDirectory) {
        if (this.isViewItem(this._directory.properties.path, item.properties)) {
            // This item is contained in this directory
            this._items.addOrUpdate(item);
        }
    }

    private onItemRemoved(item: DgFile | DgDirectory) {
        if (item === this._directory || isAncestor(item.properties.path, this._directory.properties)) {
            // This directory or its ancestor was removed, this view is gone!
            this._items.reset();
        }
        else if (this.isViewItem(this._directory.properties.path, item.properties)) {
            // This item is contained in this directory
            this._items.remove(item);
        }
    }

    private onFileUpdated(updatedEvent: [FileProperties, FileProperties]) {
        const [, newProps] = updatedEvent;

        // We don't support moving files, so we only care if this file is currently in this view
        if (isChild(this._directory.properties.path, newProps)) {
            const file = this._fileManager.getFile(newProps.path)!;
            if (this.hiddenCheck(file.properties)) {
                this._items.addOrUpdate(file);
            }
        }
    }

    private async onStatusChanged(statusChangedEvent: [DirectoryStatus, DirectoryStatus]) {
        const [oldStatus, newStatus] = statusChangedEvent;
        if (newStatus === DirectoryStatus.Uninitialized && oldStatus === DirectoryStatus.Initialized) {
            // Directory is dirty - try to refresh
            try {
                await this._fileManager.fetchFiles([this.directory.properties.path], true);
            }
            catch (error) {
                Logger.LogError(LoggerCategory.FileManager, "Failed to initialize doc set view", error);
            }
        }
    }

    private isViewItem(dirPath: string, properties: FileProperties | DirectoryProperties) {
        return isChild(dirPath, properties) && this.hiddenCheck(properties);
    }

    private hiddenCheck(properties: FileProperties | DirectoryProperties) {
        return this._showHidden || !properties.isHidden;
    }
}
